/*
 * Aurelia — Theme behaviours
 * Vanilla custom elements, no dependencies.
 * ----------------------------------------------------------- */
(function () {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const routes = theme.routes || {};
  const strings = theme.strings || {};

  /* ---------- Utilities ---------- */
  const utils = {
    formatMoney(cents) {
      const format = theme.moneyFormat || '${{amount}}';
      const value = (cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return format.replace(/\{\{\s*amount[^}]*\}\}/, value);
    },
    debounce(fn, wait) {
      let t;
      return function (...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    },
    trapFocus(container, focusFirst) {
      const focusable = container.querySelectorAll(
        'button, [href], input, select, textarea, summary, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return () => {};
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      (focusFirst || first).focus();
      function handle(e) {
        if (e.key !== 'Tab') return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
      container.addEventListener('keydown', handle);
      return () => container.removeEventListener('keydown', handle);
    },
    async fetchJSON(url, options) {
      const res = await fetch(url, options);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw err;
      }
      return res.json();
    },
  };
  theme.utils = utils;

  /* ---------- PubSub for cart updates ---------- */
  const subscribers = {};
  theme.pubsub = {
    subscribe(event, cb) {
      (subscribers[event] = subscribers[event] || []).push(cb);
      return () => {
        subscribers[event] = subscribers[event].filter((c) => c !== cb);
      };
    },
    publish(event, data) {
      (subscribers[event] || []).forEach((cb) => cb(data));
    },
  };

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    if (theme.settings && theme.settings.revealOnScroll === false) {
      document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const els = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window) || !els.length) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Lazy image fade ---------- */
  function initLazyFade() {
    document.querySelectorAll('img.lazy-fade').forEach((img) => {
      if (img.complete) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
      }
    });
  }

  /* ---------- Sticky header ---------- */
  function initStickyHeader() {
    const header = document.querySelector('[data-sticky-header]');
    if (!header || header.hasAttribute('data-sticky-disabled')) return;
    let last = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('header--scrolled', y > 40);
      if (y > last && y > 240) header.classList.add('header--hidden');
      else header.classList.remove('header--hidden');
      last = y;
    };
    window.addEventListener('scroll', utils.debounce(onScroll, 10), { passive: true });
  }

  /* =========================================================
   *  Overlay base — shared open/close for drawers & modals
   * ======================================================= */
  class OverlayElement extends HTMLElement {
    constructor() {
      super();
      this.releaseFocus = null;
      this.onKeydown = this.onKeydown.bind(this);
    }
    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
      document.addEventListener('keydown', this.onKeydown);
      const panel = this.querySelector('[data-overlay-panel]') || this;
      this.releaseFocus = utils.trapFocus(panel, this.querySelector('[data-overlay-autofocus]'));
      this.dispatchEvent(new CustomEvent('overlay:open'));
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.releaseFocus) this.releaseFocus();
      if (this.opener) this.opener.focus();
      this.dispatchEvent(new CustomEvent('overlay:close'));
    }
    onKeydown(e) {
      if (e.key === 'Escape') this.close();
    }
    bindDismissers() {
      this.querySelectorAll('[data-overlay-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      const overlay = this.querySelector('[data-overlay-backdrop]');
      if (overlay) overlay.addEventListener('click', () => this.close());
    }
  }

  /* ---------- Menu drawer ---------- */
  class MenuDrawer extends OverlayElement {
    connectedCallback() {
      this.bindDismissers();
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* ---------- Generic opener button ---------- */
  class OverlayToggle extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', () => this.trigger());
      // role="button" elements must respond to Enter/Space for keyboard users
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.trigger();
        }
      });
    }
    trigger() {
      const target = document.querySelector(this.getAttribute('data-target'));
      if (target && typeof target.open === 'function') target.open(this);
    }
  }
  customElements.define('overlay-toggle', OverlayToggle);

  /* =========================================================
   *  Cart
   * ======================================================= */
  class CartDrawer extends OverlayElement {
    connectedCallback() {
      this.bindDismissers();
      theme.pubsub.subscribe('cart:updated', (state) => this.renderContents(state));
    }
    async refresh() {
      const html = await fetch(`${routes.cart_url}?section_id=cart-drawer`).then((r) => r.text());
      this.replaceFromHTML(html);
    }
    renderContents(state) {
      if (state && state.sections && state.sections['cart-drawer']) {
        this.replaceFromHTML(state.sections['cart-drawer']);
      } else {
        this.refresh();
      }
      if (state && state.openDrawer !== false) this.open(state.opener);
    }
    replaceFromHTML(html) {
      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const newInner = fresh.querySelector('[data-cart-drawer-inner]');
      const current = this.querySelector('[data-cart-drawer-inner]');
      if (newInner && current) {
        current.innerHTML = newInner.innerHTML;
        // Only (re)bind dismissers that live inside the refreshed contents;
        // the static header close button and backdrop stay bound from connect.
        current.querySelectorAll('[data-overlay-close]').forEach((btn) =>
          btn.addEventListener('click', () => this.close())
        );
        clampClubLines();
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const step = btn.dataset.action === 'increase' ? 1 : -1;
          const min = parseInt(this.input.min || '0', 10);
          const next = Math.max(min, (parseInt(this.input.value, 10) || 0) + step);
          if (next.toString() !== this.input.value) {
            this.input.value = next;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart line items (qty change / remove) ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const row = e.target.closest('[data-cart-item]');
        if (row && e.target.matches('input[type="number"]')) {
          this.updateQuantity(row.dataset.line, e.target.value);
        }
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          this.updateQuantity(remove.dataset.line, 0);
        }
      });
    }
    async updateQuantity(line, quantity) {
      this.classList.add('is-loading');
      try {
        const state = await utils.fetchJSON(routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: parseInt(line, 10),
            quantity: parseInt(quantity, 10),
            sections: ['cart-drawer', 'main-cart', 'cart-icon-bubble'],
          }),
        });
        theme.pubsub.publish('cart:updated', { ...state, openDrawer: false });
        updateCartBubble(state.item_count);
        // Re-render page cart if present
        const mainCart = document.querySelector('[data-main-cart]');
        if (mainCart && state.sections && state.sections['main-cart']) {
          const fresh = new DOMParser().parseFromString(state.sections['main-cart'], 'text/html');
          const inner = fresh.querySelector('[data-main-cart-inner]');
          if (inner) mainCart.querySelector('[data-main-cart-inner]').innerHTML = inner.innerHTML;
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  customElements.define('cart-items', CartItems);

  function updateCartBubble(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      const changed = el.textContent !== String(count);
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
      /* Feedback pop when the count actually changes (deal-motion layer). */
      if (changed && count > 0) {
        el.classList.remove('cart-count--pop');
        void el.offsetWidth; /* restart the animation */
        el.classList.add('cart-count--pop');
      }
    });
  }

  /* ---------- ₹1 Club: server-truth quantity clamp ----------
     Club lines render with a locked qty chip (no stepper), but a double
     add via any path merges the line to qty 2 server-side. Whenever cart
     markup lands, normalize any club line back to qty 1. Terminates
     naturally: after the change the re-rendered line has qty 1. */
  let clubClampBusy = false;
  async function clampClubLines() {
    if (clubClampBusy) return;
    const row = document.querySelector('[data-cart-item][data-club="1"]');
    if (!row) return;
    const qty = parseInt(row.getAttribute('data-club-qty'), 10) || 1;
    if (qty <= 1) return;
    clubClampBusy = true;
    try {
      const state = await utils.fetchJSON(routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: parseInt(row.getAttribute('data-line'), 10),
          quantity: 1,
          sections: ['cart-drawer', 'main-cart', 'cart-icon-bubble'],
        }),
      });
      theme.pubsub.publish('cart:updated', { ...state, openDrawer: false });
      updateCartBubble(state.item_count);
      const mainCart = document.querySelector('[data-main-cart]');
      if (mainCart && state.sections && state.sections['main-cart']) {
        const fresh = new DOMParser().parseFromString(state.sections['main-cart'], 'text/html');
        const inner = fresh.querySelector('[data-main-cart-inner]');
        if (inner) mainCart.querySelector('[data-main-cart-inner]').innerHTML = inner.innerHTML;
      }
    } catch (e) {
      /* non-fatal — the extra piece still carries its own shipping */
    } finally {
      clubClampBusy = false;
    }
  }

  /* ---------- Gift options (wrap toggle + gift message) ---------- */
  class CartGifting extends HTMLElement {
    connectedCallback() {
      this.wrap = this.querySelector('[data-gift-wrap]');
      this.message = this.querySelector('[data-gift-message]');
      this.status = this.querySelector('[data-gift-status]');
      if (this.wrap) this.wrap.addEventListener('change', () => this.toggleWrap());
      if (this.message) this.message.addEventListener('input', utils.debounce(() => this.saveMessage(), 600));
    }
    async toggleWrap() {
      const id = parseInt(this.wrap.dataset.variantId, 10);
      const sections = ['cart-drawer', 'main-cart', 'cart-icon-bubble'];
      this.wrap.disabled = true;
      try {
        let state;
        if (this.wrap.checked) {
          state = await utils.fetchJSON(routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id, quantity: 1 }], sections }),
          });
        } else {
          state = await utils.fetchJSON(routes.cart_change_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id, quantity: 0, sections }),
          });
        }
        const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
        updateCartBubble(cart.item_count);
        // Refresh the drawer (subscribes to this) ...
        theme.pubsub.publish('cart:updated', { sections: state.sections, openDrawer: false });
        // ... and the cart page itself if we're on it (nothing else re-renders it).
        const mainCart = document.querySelector('[data-main-cart]');
        if (mainCart && state.sections && state.sections['main-cart']) {
          const fresh = new DOMParser().parseFromString(state.sections['main-cart'], 'text/html');
          const inner = fresh.querySelector('[data-main-cart-inner]');
          if (inner) mainCart.querySelector('[data-main-cart-inner]').innerHTML = inner.innerHTML;
        }
      } catch (e) {
        this.wrap.checked = !this.wrap.checked; // revert on failure
      } finally {
        this.wrap.disabled = false;
      }
    }
    async saveMessage() {
      try {
        await utils.fetchJSON(routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { 'Gift message': this.message.value } }),
        });
        if (this.status) {
          this.status.hidden = false;
          clearTimeout(this._t);
          this._t = setTimeout(() => { this.status.hidden = true; }, 1800);
        }
      } catch (e) {
        /* non-fatal — message just isn't saved this keystroke */
      }
    }
  }
  customElements.define('cart-gifting', CartGifting);

  /* ---------- Set adder ("Complete the set" / add multiple at once) ---------- */
  class SetAdder extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-add-set]');
      if (this.button) this.button.addEventListener('click', () => this.addSet());
    }
    async addSet() {
      const ids = (this.dataset.variantIds || '')
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !Number.isNaN(n));
      if (!ids.length) return;
      const spinner = this.button.querySelector('.spinner');
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');
      if (spinner) spinner.hidden = false;
      try {
        const items = ids.map((id) => ({ id, quantity: 1 }));
        const state = await utils.fetchJSON(routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: ['cart-drawer', 'cart-icon-bubble'] }),
        });
        const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
        updateCartBubble(cart.item_count);
        theme.pubsub.publish('cart:updated', { sections: state.sections, opener: this.button });
      } catch (e) {
        /* fail silently — the per-item links still work */
      } finally {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-busy');
        if (spinner) spinner.hidden = true;
      }
    }
  }
  customElements.define('set-adder', SetAdder);


  /* ---------- Product form (AJAX add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      if (this.button.getAttribute('aria-disabled') === 'true') return;
      this.setLoading(true);

      const formData = new FormData(this.form);
      formData.append('sections', 'cart-drawer,cart-icon-bubble');

      try {
        const state = await utils.fetchJSON(routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
        updateCartBubble(cart.item_count);

        if (theme.settings.cartType === 'drawer') {
          theme.pubsub.publish('cart:updated', { sections: state.sections, opener: this.button });
        } else {
          window.location = routes.cart_url;
        }
        this.flashSuccess();
      } catch (err) {
        this.showError((err && err.description) || strings.unavailable);
      } finally {
        this.setLoading(false);
      }
    }
    setLoading(loading) {
      this.button.classList.toggle('is-loading', loading);
      this.button.setAttribute('aria-busy', loading);
      const label = this.button.querySelector('[data-button-label]');
      const spinner = this.button.querySelector('.spinner');
      if (spinner) spinner.hidden = !loading;
      if (label) label.style.opacity = loading ? '0' : '1';
    }
    flashSuccess() {
      const label = this.button.querySelector('[data-button-label]');
      if (!label) return;
      // Only snapshot the original when no flash is pending — a double-click
      // must not capture "Added" as the text to restore.
      if (this._flashTimer) clearTimeout(this._flashTimer);
      else this._flashOriginal = label.textContent;
      label.textContent = strings.added || 'Added';
      this._flashTimer = setTimeout(() => {
        label.textContent = this._flashOriginal;
        this._flashTimer = null;
      }, 1600);
    }
    showError(msg) {
      let box = this.querySelector('[data-product-form-error]');
      if (box) {
        box.textContent = msg;
        box.hidden = false;
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.section = this.dataset.section;
      this.url = this.dataset.url;
      this.addEventListener('change', utils.debounce(() => this.onVariantChange(), 80));
    }
    get selectedOptions() {
      return Array.from(this.querySelectorAll('select, input[type="radio"]:checked')).map(
        (el) => el.value
      );
    }
    async onVariantChange() {
      const variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);
      const current = variants.find(
        (v) => JSON.stringify(v.options) === JSON.stringify(this.selectedOptions)
      );
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: current } }));

      const idInput = document.querySelector(`#variant-id-${this.section}`);
      if (current && idInput) idInput.value = current.id;

      this.updateURL(current);
      this.updatePrice(current);
      this.updateButton(current);
      this.updateMedia(current);
    }
    updateURL(variant) {
      if (!variant || !this.url) return;
      window.history.replaceState({}, '', `${this.url}?variant=${variant.id}`);
    }
    updatePrice(variant) {
      const priceEl = document.querySelector(`[data-price-for="${this.section}"]`);
      if (!priceEl) return;
      if (!variant) {
        priceEl.innerHTML = '';
        priceEl.classList.remove('price--on-sale');
        return;
      }
      const onSale = variant.compare_at_price > variant.price;
      priceEl.classList.toggle('price--on-sale', onSale);
      let html = `<span class="price__current">${utils.formatMoney(variant.price)}</span>`;
      if (onSale) {
        html += ` <s class="price__compare">${utils.formatMoney(variant.compare_at_price)}</s>`;
        // Math.floor matches Liquid's truncating divided_by, so the badge value is stable
        html += ` <span class="price__off">SAVE ${Math.floor((1 - variant.price / variant.compare_at_price) * 100)}%</span>`;
      }
      priceEl.innerHTML = html;
    }
    updateButton(variant) {
      // Scope to THIS picker's form — a quick-view picker must never touch the main PDP button
      const form = document.querySelector(`product-form[data-section="${this.section}"] [data-add-button]`);
      if (!form) return;
      const label = form.querySelector('[data-button-label]');
      if (!variant) {
        form.setAttribute('aria-disabled', 'true');
        if (label) label.textContent = strings.unavailable;
      } else if (!variant.available) {
        form.setAttribute('aria-disabled', 'true');
        if (label) label.textContent = strings.soldOut;
      } else {
        form.removeAttribute('aria-disabled');
        if (label) label.textContent = strings.addToCart;
      }
    }
    updateMedia(variant) {
      if (!variant || !variant.featured_media) return;
      // Only drive the gallery that lives in the same section as this picker
      // (a quick-view picker has none and must not move the main gallery).
      const scope = this.closest('section, .shopify-section, [data-quick-view-content]');
      const gallery = scope ? scope.querySelector('media-gallery') : null;
      if (gallery && typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(variant.featured_media.id);
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Media gallery (product) ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.thumbs = this.querySelectorAll('[data-gallery-thumb]');
      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.setActiveMedia(thumb.dataset.mediaId, true))
      );
    }
    setActiveMedia(mediaId, scroll) {
      // Thumb buttons carry data-media-id too and come FIRST in the DOM, so an
      // unscoped lookup returns the thumb and the scroll maths below then aims
      // at a 56px button instead of the slide. Always resolve inside the
      // viewport when there is one.
      const media = (this.viewport || this).querySelector(`[data-media-id="${mediaId}"]`);
      if (!media) return;
      this.thumbs.forEach((t) =>
        t.classList.toggle('is-active', t.dataset.mediaId === String(mediaId))
      );
      if (scroll === false || !this.viewport) return;
      // Horizontal carousel viewports scroll internally — safe to auto-sync on
      // variant change. Vertical layouts (thumbnail/stacked/grid) would scroll
      // the PAGE via scrollIntoView, so only do that on an explicit request
      // (thumb/arrow click passes scroll=true), never on variant change.
      const horizontal = this.viewport.scrollWidth > this.viewport.clientWidth + 1;
      if (horizontal) {
        const left = media.offsetLeft - this.viewport.offsetLeft - (this.viewport.clientWidth - media.clientWidth) / 2;
        this.viewport.scrollTo({ left, behavior: 'smooth' });
      } else if (scroll === true) {
        media.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.input) return;
      this.input.addEventListener(
        'input',
        utils.debounce(() => this.search(), 280)
      );
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        this.results.hidden = true;
        return;
      }
      try {
        const params = new URLSearchParams({
          q,
          'resources[type]': 'product,collection,page',
          'resources[limit]': 6,
          section_id: 'predictive-search',
        });
        const html = await fetch(`${routes.predictive_search_url}?${params}`).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-predictive-results]');
        this.results.innerHTML = inner ? inner.innerHTML : '';
        this.results.hidden = false;
      } catch (e) {
        this.results.hidden = true;
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Slideshow / carousel ---------- */
  class SlideShow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slides]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.index = 0;
      this.querySelectorAll('[data-slide-prev]').forEach((b) =>
        b.addEventListener('click', () => this.go(this.index - 1))
      );
      this.querySelectorAll('[data-slide-next]').forEach((b) =>
        b.addEventListener('click', () => this.go(this.index + 1))
      );
      this.dots = Array.from(this.querySelectorAll('[data-slide-dot]'));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

      if (this.hasAttribute('data-autoplay')) {
        const interval = parseInt(this.getAttribute('data-autoplay'), 10) || 6000;
        this.timer = setInterval(() => this.go(this.index + 1), interval);
        this.addEventListener('mouseenter', () => clearInterval(this.timer));
      }
      this.track.addEventListener('scroll', utils.debounce(() => this.syncFromScroll(), 80), {
        passive: true,
      });
    }
    go(i) {
      const count = this.slides.length;
      this.index = (i + count) % count;
      const slide = this.slides[this.index];
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      this.updateDots();
    }
    syncFromScroll() {
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let closest = 0;
      let min = Infinity;
      this.slides.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft + s.clientWidth / 2 - this.track.offsetLeft - center);
        if (d < min) {
          min = d;
          closest = i;
        }
      });
      this.index = closest;
      this.updateDots();
    }
    updateDots() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.index);
        dot.setAttribute('aria-current', i === this.index);
      });
    }
  }
  customElements.define('slide-show', SlideShow);

  /* ---------- Quick add ---------- */
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        this.add();
      });
    }
    async add() {
      const variantId = this.dataset.variantId;
      if (!variantId) {
        window.location = this.dataset.productUrl;
        return;
      }
      this.button.classList.add('is-loading');
      try {
        const state = await utils.fetchJSON(routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: 'cart-drawer,cart-icon-bubble' }),
        });
        const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
        updateCartBubble(cart.item_count);
        if (theme.settings.cartType === 'drawer') {
          theme.pubsub.publish('cart:updated', { sections: state.sections, opener: this.button });
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.button.classList.remove('is-loading');
      }
    }
  }
  customElements.define('quick-add', QuickAdd);

  /* ---------- Product card (variant swatches + prefetch) ---------- */
  class ProductCard extends HTMLElement {
    connectedCallback() {
      this.primary = this.querySelector('[data-card-primary]');
      this.quickAdd = this.querySelector('[data-card-quick-add]');
      this.chooseLink = this.querySelector('[data-card-choose]');
      this.swatches = Array.from(this.querySelectorAll('[data-card-swatch]'));
      this.prefetched = false;

      // Swap image / retarget quick-add when a swatch is hovered or activated.
      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          this.selectSwatch(swatch, true);
        });
        // Hover preview on pointer devices (does not lock selection on touch)
        swatch.addEventListener('mouseenter', () => this.previewSwatch(swatch));
      });
      if (this.swatches.length) {
        this.addEventListener('mouseleave', () => this.restoreSelected());
      }

      // Prefetch the PDP once, on first intent.
      const prefetch = () => this.prefetch();
      this.addEventListener('mouseenter', prefetch, { once: true });
      this.addEventListener('focusin', prefetch, { once: true });
    }

    previewSwatch(swatch) {
      // While previewing a variant, suppress the card's hover "second image"
      // so the swapped primary is what the shopper actually sees.
      this.setAttribute('data-card-swatching', '');
      this.applyImage(swatch);
    }

    restoreSelected() {
      if (!this.locked) this.removeAttribute('data-card-swatching');
      const active = this.swatches.find((s) => s.classList.contains('is-active')) || this.swatches[0];
      if (active && active.dataset.variantImage) {
        this.applyImage(active);
      } else if (this.primary && this.primary.dataset.original) {
        // Active swatch has no dedicated image — fall back to the card's
        // original featured image (restoring its responsive srcset).
        this.primary.setAttribute('src', this.primary.dataset.original);
        if (this.primary.dataset.originalSrcset) {
          this.primary.setAttribute('srcset', this.primary.dataset.originalSrcset);
        }
      }
    }

    selectSwatch(swatch, lock) {
      this.swatches.forEach((s) => {
        const on = s === swatch;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (lock) {
        // Explicit selection sticks: keep showing this variant's image even
        // after the pointer leaves a swatch and re-enters the card.
        this.locked = true;
        if (swatch.dataset.variantImage) this.setAttribute('data-card-swatching', '');
        this.retarget(swatch);
      }
      this.applyImage(swatch);
    }

    applyImage(swatch) {
      const url = swatch.dataset.variantImage;
      if (!this.primary || !url) return;
      if (this.primary.dataset.original == null) {
        this.primary.dataset.original = this.primary.getAttribute('src') || '';
        this.primary.dataset.originalSrcset = this.primary.getAttribute('srcset') || '';
        this.primary.dataset.originalAlt = this.primary.getAttribute('alt') || '';
      }
      // Variant images use a single hi-res src; drop the srcset so the browser
      // doesn't re-pick a stale candidate from the original product image.
      this.primary.setAttribute('src', url);
      this.primary.removeAttribute('srcset');
      this.primary.setAttribute('alt', swatch.dataset.variantImageAlt || this.primary.dataset.originalAlt || '');
    }

    retarget(swatch) {
      const id = swatch.dataset.variantId;
      const url = swatch.dataset.variantUrl;
      if (url) this.dataset.productUrl = url;
      if (this.quickAdd && id) {
        this.quickAdd.dataset.variantId = id;
        const available = swatch.dataset.variantAvailable === 'true';
        const btn = this.quickAdd.querySelector('button');
        if (btn) {
          btn.toggleAttribute('disabled', !available);
          btn.setAttribute('aria-disabled', available ? 'false' : 'true');
        }
      }
      if (this.chooseLink && url) this.chooseLink.setAttribute('href', url);
    }

    prefetch() {
      if (this.prefetched) return;
      const url = this.dataset.productUrl;
      if (!url) return;
      // Respect data-saver / very slow connections.
      const conn = navigator.connection;
      if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;
      this.prefetched = true;
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    }
  }
  customElements.define('product-card', ProductCard);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') return;
      const url = this.dataset.url;
      if (!url || !('IntersectionObserver' in window)) {
        this.load(url);
        return;
      }
      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          io.disconnect();
          this.load(url);
        }
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
    async load(url) {
      try {
        const text = await fetch(url).then((r) => r.text());
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim().length) {
          this.innerHTML = fresh.innerHTML;
          this.dataset.loaded = 'true';
          initReveal();
          initLazyFade();
        }
      } catch (e) {
        /* fail silently — section just stays empty */
      }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Collection grid (filter + sort) ---------- */
  class CollectionGrid extends HTMLElement {
    connectedCallback() {
      this.section = this.dataset.section;
      this.form = this.querySelector('[data-filter-form]');
      this.sort = this.querySelector('[data-sort]');
      this.facets = this.querySelector('[data-filters]');

      this.querySelectorAll('[data-filter-toggle]').forEach((btn) =>
        btn.addEventListener('click', () => this.toggleFacets())
      );
      if (this.form) {
        this.form.addEventListener('input', utils.debounce(() => this.apply(), 500));
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.apply();
        });
      }
      if (this.sort) this.sort.addEventListener('change', () => this.apply());

      // Active-filter chips (rendered by the Collection agent) carry a plain
      // href to the filtered URL with that facet removed. Intercept and apply
      // via AJAX so removing a chip never triggers a full page load.
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('a[data-facet-remove]');
        if (!chip) return;
        e.preventDefault();
        const href = chip.getAttribute('href') || '';
        const search = href.indexOf('?') > -1 ? href.slice(href.indexOf('?')) : '';
        this.apply(search, true);
      });

      window.addEventListener('popstate', (e) => {
        if (e.state && e.state.collectionGrid) this.apply(window.location.search, false);
      });
    }
    toggleFacets() {
      if (this.facets) {
        const open = this.facets.classList.toggle('is-open');
        document.body.classList.toggle('no-scroll', open && window.innerWidth < 990);
      }
    }
    buildParams() {
      const params = new URLSearchParams(this.form ? new FormData(this.form) : '');
      // drop empty price inputs
      for (const [k, v] of Array.from(params.entries())) {
        if (v === '') params.delete(k);
      }
      if (this.sort && this.sort.value) params.set('sort_by', this.sort.value);
      return params.toString();
    }
    async apply(searchString, push) {
      const query = typeof searchString === 'string' ? searchString.replace(/^\?/, '') : this.buildParams();
      const base = window.location.pathname;
      this.classList.add('is-loading');
      try {
        const url = `${base}?${query}`;
        const text = await fetch(`${url}${query ? '&' : ''}section_id=${this.section}`).then((r) => r.text());
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const newGrid = doc.querySelector('[data-product-grid-container]');
        const newFacets = doc.querySelector('[data-filters]');
        const newCount = doc.querySelector('[data-product-count]');
        if (newGrid) this.querySelector('[data-product-grid-container]').innerHTML = newGrid.innerHTML;
        if (newFacets && this.facets) {
          const wasOpen = this.facets.classList.contains('is-open');
          this.facets.innerHTML = newFacets.innerHTML;
          if (wasOpen) this.facets.classList.add('is-open');
          this.form = this.querySelector('[data-filter-form]');
        }
        if (newCount) this.querySelector('[data-product-count]').textContent = newCount.textContent;
        if (push !== false) {
          window.history.pushState({ collectionGrid: true }, '', url);
        }
        initReveal();
        initLazyFade();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {
        if (this.form) this.form.submit();
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  customElements.define('collection-grid', CollectionGrid);

  /* =========================================================
   *  Wishlist — localStorage favourites: heart toggles on cards,
   *  a header count, and a hydrated /pages/wishlist page.
   * ======================================================= */
  const Wishlist = (() => {
    const KEY = 'kundan:wishlist';
    const read = () => {
      try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; }
    };
    const write = (list) => {
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* private mode */ }
    };
    const has = (handle) => read().indexOf(handle) !== -1;
    const toggle = (handle) => {
      const list = read();
      const i = list.indexOf(handle);
      if (i === -1) list.unshift(handle); else list.splice(i, 1);
      write(list);
      return i === -1; // now saved?
    };
    const syncCount = () => {
      const n = read().length;
      document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        el.textContent = n;
        el.classList.toggle('is-empty', n === 0);
      });
    };
    const syncButtons = (root) => {
      (root || document).querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
        const on = has(btn.dataset.productHandle);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    };
    return { read, toggle, has, syncCount, syncButtons };
  })();
  theme.wishlist = Wishlist;

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const saved = Wishlist.toggle(btn.dataset.productHandle);
    btn.classList.toggle('is-active', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.classList.remove('is-pulse');
    void btn.offsetWidth;
    btn.classList.add('is-pulse');
    Wishlist.syncCount();
    if (!saved) {
      const grid = document.querySelector('[data-wishlist-grid]');
      if (grid) {
        const card = grid.querySelector('[data-wishlist-card="' + btn.dataset.productHandle + '"]');
        if (card) card.remove();
        if (!grid.children.length) hydrateWishlistPage();
      }
    }
  });

  async function hydrateWishlistPage() {
    const section = document.querySelector('[data-wishlist-page]');
    if (!section) return;
    const grid = section.querySelector('[data-wishlist-grid]');
    const empty = section.querySelector('[data-wishlist-empty]');
    const loading = section.querySelector('[data-wishlist-loading]');
    const base = section.getAttribute('data-card-url');
    const handles = Wishlist.read();
    const showEmpty = () => {
      if (loading) loading.hidden = true;
      if (grid) grid.hidden = true;
      if (empty) empty.hidden = false;
    };
    if (!handles.length || !base) return showEmpty();
    if (loading) loading.hidden = false;
    const cards = await Promise.all(handles.map((h) =>
      fetch(base.replace('__handle__', encodeURIComponent(h)))
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          if (!html) return '';
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector('.product-card');
          if (!card) return '';
          card.setAttribute('data-wishlist-card', h);
          return card.outerHTML;
        })
        .catch(() => '')
    ));
    const markup = cards.filter(Boolean).join('');
    if (loading) loading.hidden = true;
    if (!markup) return showEmpty();
    grid.innerHTML = markup;
    grid.hidden = false;
    if (empty) empty.hidden = true;
    Wishlist.syncButtons(grid);
    initReveal();
    initLazyFade();
  }

  /* =========================================================
   *  Quick view — fetch a compact product view into an overlay.
   * ======================================================= */
  class QuickView extends OverlayElement {
    connectedCallback() {
      this.bindDismissers();
      this.body = this.querySelector('[data-quick-view-body]');
      theme.pubsub.subscribe('cart:updated', () => {
        if (this.classList.contains('is-open')) this.close();
      });
    }
    async load(url, opener) {
      if (!url || !this.body) return;
      this.body.innerHTML = '<div class="quick-view__loading"><span class="spinner"></span></div>';
      this.open(opener);
      try {
        const clean = url.split('#')[0];
        const sep = clean.indexOf('?') === -1 ? '?' : '&';
        const html = await fetch(`${clean}${sep}section_id=quick-view`).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-quick-view-content]');
        if (content) {
          this.body.innerHTML = content.outerHTML;
        } else {
          window.location = url;
        }
      } catch (e) {
        this.body.innerHTML = '<p class="quick-view__error">Couldn’t load this. <a href="' + url + '">View product →</a></p>';
      }
    }
  }
  customElements.define('quick-view', QuickView);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (!trigger) return;
    e.preventDefault();
    const qv = document.querySelector('quick-view');
    if (qv && typeof qv.load === 'function') qv.load(trigger.dataset.productUrl, trigger);
  });

  /* ---------- Elra mixed-type headlines (minimal skin only) ----------
     Wraps the last word of each section title in a .title-accent span so
     CSS can set it in the italic serif accent face ("Most *Bought*").
     Pure presentation: text content and reading order are unchanged. */
  function initTitleAccents() {
    if (!document.body.classList.contains('theme--minimal')) return;
    document.querySelectorAll('.section-header__title').forEach((el) => {
      if (el.dataset.accented || el.children.length) return;
      const words = (el.textContent || '').trim().split(/\s+/);
      if (words.length < 2) return;
      el.dataset.accented = 'true';
      const last = words.pop();
      el.textContent = words.join(' ') + ' ';
      const span = document.createElement('span');
      span.className = 'title-accent';
      span.textContent = last;
      el.appendChild(span);
    });
  }

  /* ---------- Init ---------- */
  function init() {
    initReveal();
    initLazyFade();
    initStickyHeader();
    initTitleAccents();
    Wishlist.syncCount();
    Wishlist.syncButtons();
    hydrateWishlistPage();
    clampClubLines();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run on Shopify theme editor section load
  document.addEventListener('shopify:section:load', () => {
    initReveal();
    initLazyFade();
    initTitleAccents();
    Wishlist.syncCount();
    Wishlist.syncButtons();
    hydrateWishlistPage();
  });
})();
